/**
 * AskSession Component
 *
 * This is the main ask session page component for workspaces.
 * It provides:
 * - Real-time chat interface with AI assistant
 * - Message history and session management
 * - Document context and selection
 * - Search and navigation within messages
 * - Message pinning and export functionality
 * - Keyboard shortcuts for power users
 * - Template management for common queries
 * - PDF document viewing
 *
 * The component manages session state, handles message sending/receiving,
 * and integrates with the Ask API for AI-powered conversations.
 */

// ============= Imports =============

// React hooks
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import React from 'react';

// Routing
import { useParams, useNavigate } from 'react-router-dom';

// Styled components
import {
  PageContainer,
  ContentSectionContainer,
  ContentSection,
  ActiveDocuments,
  ContextPanel,
  ThreadSummary,
  ActiveDocumentsList,
  ActiveDocumentsItem,
  ThreadSummaryContent,
  ThreadSummaryItem,
  ThreadSummaryItemContent,
  ThreadSummaryHeader,
  ActiveDocumentsHeader,
  DocumentName,
  DocumentMeta,
  KeyboardShortcutsContent,
  ShortcutItem,
  ShortcutDescription,
  ShortcutKey,
  PinnedMessages,
  PinnedMessagesHeader,
  PinnedMessagesContent,
  PinnedMessageItem,
  PinnedMessagePreview,
  PinnedMessageMeta,
  SearchBar,
  SearchInput,
  SearchMatchCount,
  SearchNavButton,
  SearchCloseButton,
  AddDocumentButton,
  AddDocumentText,
  AddDocumentButtonWrapper,
  DocumentCardClickable,
  SynopsisText,
  SynopsisToggle,
  DocumentAddedDate,
  RemoveDocumentButton,
  LinkedBadge,
  ShortcutKeysContainer,
  ShortcutClickHint,
  ExportButtonContainer,
  ExportButton,
  ModalContent,
  ModalSection,
  ModalSectionTitle,
  ModalShortcutRow,
  ModalKbd,
  ModalFeatureList,
  TemplatesHeader,
  NewTemplateButton,
  EmptyTemplatesMessage,
  TemplatesList,
  TemplateItem,
  TemplateInput,
  TemplateTextarea,
  TemplateActions,
  UseTemplateButton,
  DeleteTemplateButton,
  PdfViewerError,
  PdfViewerLayout,
  PdfViewerMain,
  PdfViewerCitationsContainer,
  PdfViewerCitationsPanel,
  PdfViewerCitationsPanelTitle,
  PdfViewerCitationItem,
  PdfViewerCitationRef,
  PdfViewerCitationSnippet,
  PdfViewerCitationPage,
  PersonaModalContent,
  PersonaCardGroup,
  PersonaCard,
  PersonaIcon,
  PersonaName,
  PersonaDescription,
  PersonaTags,
  PersonaTag,
  PersonaModalActions,
  PersonaSubmitButton,
  PersonaCancelButton,
  PersonaLoadingContainer,
  PersonaErrorContainer,
  ExpandToggleButton,
  CompactDocumentsGrid,
  CompactDocumentIcon,
  DocumentIcon,
  CompactDocumentContent,
  CompactDocumentLabel,
  LinkedIndicator,
  SessionsSidebar,
  SessionsSidebarHeader,
  SessionsSidebarTitle,
  LeftSidebarContainer,
  RightSidebarContainer,
  SessionList,
  SessionDateGroup,
  SessionDateGroupTitle,
  SessionListItem,
  SessionListItemTitle,
  ChatTopBar,
  SidebarToggleButton,
  ToolsPanelHeader,
} from './AskSession.styles';
import { RiCollapseDiagonal2Fill } from 'react-icons/ri';
import { RiExpandDiagonal2Fill } from 'react-icons/ri';

// Component library
import {
  Headers,
  LoadingSpinner,
  Modal,
  Accordion,
} from '@tw/tw-component-library';

// Layout components
import { WorkspaceHeaderLayout } from '@/components/layout/WorkspaceHeaderLayout';
import { useAskRelayHubHandlers } from '@/hooks/signalR/useAskRelayHubHandlers';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';

// Document components
import { PdfViewer } from '@/components/common/Documents/PdfViewer/PdfViewer';

// Ask components
import { AskMessage } from '@/components/common/Ask/Ask';
import { EnhancedTextArea } from '@/components/common/Ask/EnhancedTextArea/EnhancedTextArea';
import {
  ChatMessages,
  getCitationColor,
} from '@/components/common/Ask/ChatMessages';
import { DocumentSelector } from '@/components/common/Ask/DocumentSelector';
import { UploadProcessingIndicator } from '@/components/common/Ask/UploadProcessingIndicator';

import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';

// Contexts
import { UseAuth } from '@/contexts/AuthContext';
import { useDebug } from '@/contexts/DebugContext';

// Types
import { DataRoomFile } from '@/types/documents/documents.types';
import {
  AskSessionSession,
  AskSessionCitation,
} from '@/apis/ask/askHistory/types';

// APIs
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';
import { downloadDocumentStream } from '@/apis/data/document/get/downloadDocumentStream';
import { getCitationDocument } from '@/apis/data/document/get/getCitationDocument';
import {
  getCitationDocumentFromCache,
  setCitationDocumentCache,
} from '@/utils/documents/citationDocumentCache';
import { getAskSession } from '@/apis/ask/askHistory/get/getAskSession';
import { getAskSessionMessages } from '@/apis/ask/askHistory/get/getAskSessionMessages';
import { createAskSession } from '@/apis/ask/askHistory/post/createAskSession';
import { invokeAsk } from '@/apis/ask/ask/post/invokeAsk';
import { AskType } from '@/apis/ask/ask/types';
import { getAskPersonas } from '@/apis/ask/askPersona/get/getAskPersonas';
import { AskPersona } from '@/apis/ask/askPersona/types';
import { linkFileToAsk } from '@/apis/data/dataRoom/post/linkFileToAsk';
import { getAskSessionsByWorkspace } from '@/apis/ask/askHistory/get/getAskSessionsByWorkspace';

// Utilities
import {
  transformAllUserWorkspaces,
  TransformedUserWorkspace,
} from '@/utils/data/transformWorkSpaceItems';
import { getFileIcon } from '@/utils/documents/documentHelpers';

// Local utilities and constants
import {
  transformApiMessageToComponent,
  transformApiMessages,
  getFileExtension,
  calculateTotalTokens,
  formatDate,
  truncateText,
  withTimeout,
  isAbortError,
} from './AskSession.utils';
import {
  DRAFT_STORAGE_PREFIX,
  TEMPLATES_STORAGE_KEY,
  DRAFT_AUTO_SAVE_INTERVAL_MS,
  DRAFT_RESTORE_DELAY_MS,
  GENERATION_CANCELLED_MESSAGE,
  STREAMING_WORD_DELAY_MS,
  MAX_SYNOPSIS_PREVIEW_LENGTH,
  MAX_PINNED_MESSAGE_PREVIEW_LENGTH,
  KEYBOARD_SHORTCUTS,
  COMMAND_TEMPLATES,
} from './AskSession.constants';

// Types
import { TextAreaRef, MessageTemplate } from './AskSession.types';
import type { Segment } from '@/components/common/Ask/EnhancedTextArea/types';
import { useAskSessionSignalRHandlers } from './AskSession.signalRHandlers';
import { useAskSessionTypewriterEffect } from './AskSession.streaming';
import { useAskSessionComputedValues } from './AskSession.computedValues';
import { useAskSessionRenderHelpers } from './AskSession.renderHelpers';
import { useAskSessionNavigationHandlers } from './AskSession.navigationHandlers';

// ============= Component =============

/**
 * AskSession Component
 *
 * Main component for managing ask sessions within a workspace.
 * Handles chat interface, message management, and document context.
 *
 * @returns JSX.Element - The rendered ask session page
 */
export const AskSession: React.FC = () => {
  // ============= Hooks =============

  const { id: workspaceId, sessionId } = useParams<{
    id: string;
    sessionId?: string;
  }>();
  const { getAccessToken, currentUser } = UseAuth();
  const navigate = useNavigate();
  const { debug: debugState, canShowDebug } = useDebug();

  // For non-debug users: always allow toasts, but never enable logging.
  const enableAskToasts = canShowDebug ? debugState.enableToasts : true;
  const enableAskLogging = canShowDebug && debugState.enableLogging;

  const { handleError, handleSuccess } = useWorkspaceErrorHandler(workspaceId);

  // ============= Refs =============

  const textAreaRef = useRef<TextAreaRef | null>(null);

  // ============= State =============

  // Workspace and session state
  const [workspaceData, setWorkspaceData] =
    useState<TransformedUserWorkspace | null>(null);
  const [askSession, setAskSession] = useState<AskSessionSession | null>(null);
  const [workspaceAskSessions, setWorkspaceAskSessions] = useState<
    AskSessionSession[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Message state
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [responseStartTime, setResponseStartTime] = useState<
    number | undefined
  >();
  const [failedMessageIds, setFailedMessageIds] = useState<Set<string>>(
    new Set(),
  );

  // Streaming: each STREAM has full content (cumulative). Delta = newStream.replace(previousStream, ''); we animate only the delta.
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const streamingMessageIdRef = useRef<string | null>(null);
  /** Status message shown in typing indicator (e.g. "Determining audience in Ask."). */
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  /** Previous cumulative content from server; delta = newFull.replace(this, ''). */
  const lastStreamContentRef = useRef('');
  /** Content we've already revealed in the UI (message content). */
  const displayedContentRef = useRef('');
  /** Pending delta text to typewriter (deltas appended here, effect consumes word-by-word). */
  const toRevealRef = useRef('');

  // ============= Send Guards =============
  // Prevent overlapping sends (fast double-click / fast Enter) from racing SignalR group join / invoke.
  const sendInFlightRef = useRef(false);
  // Locks the input until the streamed message signals completion.
  const assistantGenerationInProgressRef = useRef(false);
  const pendingSendRef = useRef<{
    segments: Segment[];
    documentIds?: string[];
    mentionedUserIds?: string[];
  } | null>(null);

  // Tracks whether we actually reached the `invokeAsk` call for the current send attempt.
  const activeSendAttemptIdRef = useRef<string | null>(null);
  const invokeStartedRef = useRef(false);
  // Used to detect "stuck" streams (no STREAM events for a while).
  const lastStreamActivityAtRef = useRef<number>(0);
  const invokeStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const fallbackRefreshTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const invokeAbortControllerRef = useRef<AbortController | null>(null);
  /** When true, ignore late SignalR stream/completion events from a cancelled send. */
  const generationCancelledRef = useRef(false);

  // Document state
  const [selectedDocuments, setSelectedDocuments] = useState<DataRoomFile[]>(
    [],
  );
  const [expandedSynopsis, setExpandedSynopsis] = useState<
    Record<string, boolean>
  >({});
  const [selectedDocumentForView, setSelectedDocumentForView] =
    useState<DataRoomFile | null>(null);
  const [pdfStreamUrl, setPdfStreamUrl] = useState<string | null>(null);
  const [initialPdfPage, setInitialPdfPage] = useState<number | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [showPdfViewerModal, setShowPdfViewerModal] = useState(false);
  const [pdfViewerCitations, setPdfViewerCitations] = useState<
    AskSessionCitation[] | null
  >(null);
  const pdfViewerRef = useRef<{ setPage: (pageNumber: number) => void } | null>(
    null,
  );
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [uploadingFileNames, setUploadingFileNames] = useState<string[]>([]);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | undefined
  >();

  // UI state
  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0);
  const [pinnedMessageIds, setPinnedMessageIds] = useState<Set<string>>(
    new Set(),
  );
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [activeDocumentsExpanded, setActiveDocumentsExpanded] = useState(false);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const [themeMode, setThemeMode] = useState<'default' | 'compact'>('default');
  const [isEnterSendBlockedByTutorial, setIsEnterSendBlockedByTutorial] =
    useState(false);

  // Persona selection state
  const [showPersonaModal, setShowPersonaModal] = useState(false);

  useEffect(() => {
    const tutorialStepTitle = 'Send a prompt referencing the document';

    const updateTutorialKeyboardBlockState = () => {
      const shouldBlock =
        document.body.textContent?.includes(tutorialStepTitle) ?? false;
      setIsEnterSendBlockedByTutorial(shouldBlock);
    };

    updateTutorialKeyboardBlockState();

    const observer = new MutationObserver(() => {
      updateTutorialKeyboardBlockState();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // ============= Data Fetching =============

  /**
   * Normalizes file names for matching upload events, ignoring case and extension.
   * This lets us match uploads that are converted server-side (e.g. .docx → .pdf)
   * but keep the same base name.
   */
  const normalizeFileName = useCallback((name: string | undefined | null) => {
    if (!name) return '';
    const lower = name.toLowerCase().trim();
    return lower.replace(/\.[^./\\]+$/, '');
  }, []);

  /**
   * Fetches workspace data and transforms it for use in the component.
   */
  const getWorkspaceData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getWorkspace({
        getAccessToken: getAccessToken,
        workspaceId: workspaceId || '',
      });

      const transformed = await transformAllUserWorkspaces(
        [data],
        getAccessToken,
      );
      setWorkspaceData(transformed[0]);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load workspace data');
      setError(error);
      const response =
        (err as { response?: unknown; body?: unknown })?.response ??
        (err as { response?: unknown; body?: unknown })?.body;
      handleError(error, 'Failed to load workspace', response, enableAskToasts);
      console.error('Failed to load workspace data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, getAccessToken, handleError, enableAskToasts]);

  /**
   * Fetches ask session data and messages.
   */
  const fetchAskSessionData = useCallback(async () => {
    if (!sessionId) return;

    try {
      const session = await getAskSession({
        getAccessToken,
        askSessionId: sessionId,
      });
      setAskSession(session);

      // Fetch messages for this session
      const sessionMessages = await getAskSessionMessages({
        getAccessToken,
        askSessionId: sessionId,
      });

      // Transform API messages to component messages
      const currentUserId = currentUser?.userId || currentUser?.oid || '';
      const transformedMessages = transformApiMessages(
        sessionMessages,
        currentUserId,
      );

      setMessages(transformedMessages);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load session');
      const response =
        (err as { response?: unknown; body?: unknown })?.response ??
        (err as { response?: unknown; body?: unknown })?.body;
      handleError(error, 'Failed to load session', response, enableAskToasts);
      console.error('Failed to load ask session:', err);
    }
  }, [sessionId, getAccessToken, currentUser, handleError, enableAskToasts]);

  const fetchWorkspaceAskSessions = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const sessions = await getAskSessionsByWorkspace({
        getAccessToken,
        workspaceId,
      });
      sessions.sort(
        (a, b) =>
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
      );
      setWorkspaceAskSessions(sessions);
    } catch (err) {
      console.error('Failed to load workspace ask sessions:', err);
    }
  }, [workspaceId, getAccessToken]);

  // ============= SignalR Event Handlers =============

  // Memoize the event handler callback to prevent unnecessary re-subscriptions
  const handleAskEventReceived = useCallback(
    (event: string, data: unknown, parsed?: Record<string, unknown>) => {
      const currentUserId = currentUser?.userId || currentUser?.oid || '';

      const dataRecord =
        data && typeof data === 'object'
          ? (data as Record<string, unknown>)
          : {};
      const parsedRecord: Record<string, unknown> = parsed ?? {};

      if (
        generationCancelledRef.current &&
        event.startsWith('ASK_SESSION_MESSAGE')
      ) {
        return;
      }

      const getStatusFromEventData = (defaultStatus: string): string => {
        const param2 = dataRecord.param2;
        if (typeof param2 === 'string' && param2.trim()) return param2;

        const param1 = dataRecord.param1;
        if (Array.isArray(param1) && typeof param1[1] === 'string') {
          return param1[1];
        }

        return defaultStatus;
      };

      const isAskSessionMessage = (
        value: unknown,
      ): value is import('@/apis/ask/askHistory/types').AskSessionMessage => {
        return (
          typeof value === 'object' &&
          value !== null &&
          'id' in value &&
          'content' in value &&
          'timestamp' in value
        );
      };

      // Handle ASK_SESSION_MESSAGE_RECIEVED - user message echoed from server; show status
      if (event === 'ASK_SESSION_MESSAGE_RECIEVED') {
        const statusText = getStatusFromEventData(
          'Message received. Fetching response...',
        );
        setProcessingStatus(statusText);
        const messageData = parsedRecord.messageData;
        if (isAskSessionMessage(messageData)) {
          const transformedMessage = transformApiMessageToComponent(
            messageData,
            currentUserId,
          );
          setMessages((prev) => {
            const exists = prev.some((msg) => msg.id === transformedMessage.id);
            if (exists) return prev;
            // Replace optimistic user message with server message (same content) so we keep real id
            const lastUser = [...prev]
              .reverse()
              .find((m) => m.senderId === 'currentUser');
            if (lastUser && lastUser.content === transformedMessage.content) {
              return prev.map((m) =>
                m.id === lastUser.id ? transformedMessage : m,
              );
            }
            return [...prev, transformedMessage];
          });
        }
      }

      // Status events: show server status string in typing indicator (param2 = status string)
      if (event === 'ASK_SESSION_MESSAGE_AUDIENCE') {
        const status = getStatusFromEventData('Determining audience in Ask.');
        setProcessingStatus(status);
      }
      if (event === 'ASK_SESSION_MESSAGE_INTENT') {
        const status = getStatusFromEventData(
          'Determining users intent in Ask.',
        );
        setProcessingStatus(status);
      }
      if (event === 'ASK_SESSION_MESSAGE_DOCUMENT_CONTEXT') {
        const status = getStatusFromEventData(
          'Understanding document context in Ask.',
        );
        setProcessingStatus(status);
      }
      if (event === 'ASK_SESSION_MESSAGE_DOCUMENTS') {
        const status = getStatusFromEventData(
          'Determining available documents in Ask.',
        );
        setProcessingStatus(status);
      }

      // Handle ASK_SESSION_MESSAGE_COMPLETION - streaming is finished; keep rendered stream content, only apply metadata (tokenUsage, citations) from payload.
      if (event === 'ASK_SESSION_MESSAGE_COMPLETION') {
        const completionMessageData = parsedRecord.messageData;
        const activeStreamingId = streamingMessageIdRef.current;

        const completionMessageIdRaw =
          parsedRecord.messageId ??
          (completionMessageData as { messageId?: unknown } | null)
            ?.messageId ??
          (completionMessageData as { id?: unknown } | null)?.id;

        const completedMessageId =
          completionMessageIdRaw === null ||
          completionMessageIdRaw === undefined
            ? undefined
            : String(completionMessageIdRaw);

        // Only unlock/finalize for the currently streaming message when we know the ID.
        if (
          activeStreamingId &&
          completedMessageId &&
          completedMessageId !== activeStreamingId
        ) {
          return;
        }

        const targetMessageId = completedMessageId ?? activeStreamingId;

        const transformedMessage =
          completionMessageData && targetMessageId
            ? transformApiMessageToComponent(
                completionMessageData as import('@/apis/ask/askHistory/types').AskSessionMessage,
                currentUserId,
              )
            : null;

        // Finalize using the completion payload content.
        // This avoids markdown being finalized with an incomplete string when
        // completion arrives before the typewriter has revealed the last chars.
        const completionContent = transformedMessage?.content ?? '';
        const lastStreamContent = lastStreamContentRef.current ?? '';
        const finalStreamedContent =
          completionContent.length >= lastStreamContent.length
            ? completionContent
            : lastStreamContent;

        if (targetMessageId && transformedMessage) {
          setMessages((prev) => {
            const messageExists = prev.some(
              (msg) => msg.id === targetMessageId,
            );
            if (!messageExists) return [...prev, transformedMessage];

            return prev.map((msg) =>
              msg.id !== targetMessageId
                ? msg
                : {
                    ...msg,
                    content:
                      finalStreamedContent && finalStreamedContent.length > 0
                        ? finalStreamedContent
                        : msg.content,
                    tokenUsage: transformedMessage.tokenUsage ?? msg.tokenUsage,
                    citations: transformedMessage.citations ?? msg.citations,
                  },
            );
          });
        }

        assistantGenerationInProgressRef.current = false;
        setIsSendingMessage(false);
        setResponseStartTime(undefined);
        setProcessingStatus(null);
        setStreamingMessageId(null);

        lastStreamContentRef.current = '';
        displayedContentRef.current = '';
        toRevealRef.current = '';
        lastStreamActivityAtRef.current = 0;
      }

      // STREAM_STARTED: new message—reset stream refs so we start fresh for this message.
      if (event === 'ASK_SESSION_MESSAGE_STREAM_STARTED') {
        const messageId =
          typeof parsedRecord.messageId === 'string'
            ? parsedRecord.messageId
            : undefined;
        const askSessionId =
          typeof parsedRecord.askSessionId === 'string'
            ? parsedRecord.askSessionId
            : '';

        if (messageId) {
          lastStreamContentRef.current = '';
          displayedContentRef.current = '';
          toRevealRef.current = '';
          lastStreamActivityAtRef.current = Date.now();
          setStreamingMessageId(messageId);
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === messageId)) return prev;
            const placeholderOnly: import('@/apis/ask/askHistory/types').AskSessionMessage =
              {
                id: messageId,
                _etag: '',
                userId: 'assistant',
                userName: 'Assistant',
                askSessionId: askSessionId,
                content: '', // never use stream data here
                timestamp: new Date().toISOString(),
                role: 'Assistant',
                functionsUsed: [],
                prompt: '',
                citations: null,
                type: '0',
                userFeedback: '',
                citationsUsed: {},
                stepIndex: null,
                userInputValidated: null,
                userInput: null,
              };
            return [
              ...prev,
              transformApiMessageToComponent(placeholderOnly, currentUserId),
            ];
          });
        }
      }

      // STREAM_ENDED: one segment finished; more may follow or COMPLETION next. No content display change.
      if (event === 'ASK_SESSION_MESSAGE_STREAM_ENDED') {
        // Keep typing indicator until COMPLETION.
      }

      // STREAM: cumulative content. Delta = newStream.replace(previousStream, ''); append delta to pending and continue animation with the difference.
      if (event === 'ASK_SESSION_MESSAGE_STREAM') {
        const messageId =
          typeof parsedRecord.messageId === 'string'
            ? parsedRecord.messageId
            : undefined;
        const askSessionId =
          typeof parsedRecord.askSessionId === 'string'
            ? parsedRecord.askSessionId
            : '';
        const streamContent = parsedRecord.streamContent;

        if (messageId && streamContent !== undefined) {
          const newFull = String(streamContent ?? '');
          const previousStream = lastStreamContentRef.current;
          const delta = newFull.replace(previousStream, '');
          lastStreamContentRef.current = newFull;
          toRevealRef.current += delta;
          lastStreamActivityAtRef.current = Date.now();
          setStreamingMessageId(messageId);
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === messageId)) return prev;
            const placeholderOnly: import('@/apis/ask/askHistory/types').AskSessionMessage =
              {
                id: messageId,
                _etag: '',
                userId: 'assistant',
                userName: 'Assistant',
                askSessionId: askSessionId,
                content: '',
                timestamp: new Date().toISOString(),
                role: 'Assistant',
                functionsUsed: [],
                prompt: '',
                citations: null,
                type: '0',
                userFeedback: '',
                citationsUsed: {},
                stepIndex: null,
                userInputValidated: null,
                userInput: null,
              };
            return [
              ...prev,
              transformApiMessageToComponent(placeholderOnly, currentUserId),
            ];
          });
        }
      }

      // Handle ASK_SESSION_THREAD_SYNOPSIS - update Thread Summary in context panel
      if (event === 'ASK_SESSION_THREAD_SYNOPSIS') {
        const synopsisFromParsed =
          typeof parsedRecord.synopsis === 'string'
            ? parsedRecord.synopsis
            : undefined;

        const synopsisData = parsedRecord.synopsisData;
        const askThreadSynopsisFromSynopsisData =
          typeof synopsisData === 'object' && synopsisData !== null
            ? (synopsisData as Record<string, unknown>).askThreadSynopsis
            : undefined;

        const synopsisFromSynopsisData =
          typeof askThreadSynopsisFromSynopsisData === 'string'
            ? askThreadSynopsisFromSynopsisData
            : undefined;

        const param1 = parsedRecord.param1;
        const askThreadSynopsisFromParam1 =
          typeof param1 === 'object' && param1 !== null
            ? (param1 as Record<string, unknown>).askThreadSynopsis
            : undefined;

        const synopsisFromParam1 =
          typeof askThreadSynopsisFromParam1 === 'string'
            ? askThreadSynopsisFromParam1
            : undefined;

        const synopsis =
          synopsisFromParsed ?? synopsisFromSynopsisData ?? synopsisFromParam1;

        if (synopsis) {
          setAskSession((prev) => {
            if (!prev) return prev;
            return { ...prev, askThreadSynopsis: synopsis };
          });
        }
      }
    },
    [
      currentUser?.userId,
      currentUser?.oid,
      // Note: messages and askSession are accessed via closures in setState callbacks
      // which is safe and doesn't require them in dependencies
    ],
  );

  // Keep a ref in sync with streamingMessageId so the typewriter effect
  // can read the latest id without re-subscribing its interval.
  useEffect(() => {
    streamingMessageIdRef.current = streamingMessageId;
  }, [streamingMessageId]);

  // Wire Ask relay hub handlers for this workspace/session so that
  // Ask-specific SignalR events (message received, completion, documents, etc.)
  // are always handled when viewing an Ask session.
  // Expose a helper so we can re-join the Ask session group before sending messages
  const { ensureAskSessionGroupJoin } = useAskRelayHubHandlers({
    askSessionId: sessionId,
    userId: currentUser?.userId || '',
    onEventReceived: handleAskEventReceived,
    enableToasts: enableAskToasts,
    enableLogging: enableAskLogging,
  });

  // Typewriter: single interval that consumes pending delta (toRevealRef)
  // word-by-word, appends to displayedContentRef, and updates message content.
  // Using a ref for the streaming message id avoids resubscribing the interval
  // on every streaming event and keeps renders tighter under rapid streams.
  useAskSessionTypewriterEffect({
    streamingMessageIdRef,
    toRevealRef,
    displayedContentRef,
    setMessages,
    delayMs: STREAMING_WORD_DELAY_MS,
  });

  // Memoize the data refresh callback to prevent unnecessary re-subscriptions
  const handleWorkspaceDataRefresh = useCallback(async () => {
    await getWorkspaceData();
    if (sessionId) {
      await fetchAskSessionData();
    }
    await fetchWorkspaceAskSessions();
  }, [
    getWorkspaceData,
    fetchAskSessionData,
    fetchWorkspaceAskSessions,
    sessionId,
  ]);

  // Set up SignalR for workspace messages to handle file uploads
  useSignalRHub({ hubName: 'workspaceRelayHub' });

  const currentUserIdForSignalR = currentUser?.userId || currentUser?.oid || '';

  const { handleWorkspaceEventReceived } = useAskSessionSignalRHandlers({
    currentUserId: currentUserIdForSignalR,
    sessionId,
    setMessages,
    setProcessingStatus,
    setAskSession,
    setStreamingMessageId,
    lastStreamContentRef,
    displayedContentRef,
    toRevealRef,

    uploadingFileNames,
    setUploadingFileNames,
    normalizeFileName,

    getWorkspaceData,
    fetchAskSessionData,
  });

  // Handle workspace SignalR events for file uploads
  useWorkspaceRelayHubHandlers({
    workspaceId,
    onDataRefresh: handleWorkspaceDataRefresh,
    enableToasts: false, // We'll handle toasts ourselves
    enableLogging: enableAskLogging,
    onEventReceived: handleWorkspaceEventReceived,
  });

  const [personas, setPersonas] = useState<AskPersona[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(
    null,
  );
  const [isLoadingPersonas, setIsLoadingPersonas] = useState(false);
  const [personaError, setPersonaError] = useState<Error | null>(null);

  // ============= Computed Values =============
  const {
    filteredMessages,
    searchMatches,
    users,
    pinnedMessagesList,
    isMac,
    documentNameToIdMap,
  } = useAskSessionComputedValues({
    messages,
    searchQuery,
    pinnedMessageIds,
    currentUserDisplayName: currentUser?.userDisplayName,
    workspaceData,
    selectedDocuments,
  });

  const groupedWorkspaceAskSessions = useMemo(() => {
    const groups = new Map<string, AskSessionSession[]>();

    workspaceAskSessions.forEach((session) => {
      const date = new Date(session.createdOn);
      const groupLabel = date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      const existing = groups.get(groupLabel) || [];
      existing.push(session);
      groups.set(groupLabel, existing);
    });

    return Array.from(groups.entries()).map(([label, sessions]) => ({
      label,
      sessions,
    }));
  }, [workspaceAskSessions]);

  // ============= Effects =============

  /**
   * Initializes selectedDocuments from askSession.files when both are available.
   */
  useEffect(() => {
    if (!askSession || !workspaceData) return;

    // Get linked file IDs from ask session
    const linkedFileIds = new Set<string>();
    if (askSession.files && Array.isArray(askSession.files)) {
      askSession.files.forEach((file) => {
        const fileId =
          typeof file === 'string'
            ? file
            : (file as DataRoomFile)?.dataRoomFileId ||
              (file as DataRoomFile)?.id;
        if (fileId) {
          linkedFileIds.add(fileId);
        }
      });
    }

    // Get all documents from workspace
    const allDocuments =
      workspaceData.dataRoom?.workspaceDataRoomFiles?.items?.filter(
        (item): item is DataRoomFile => {
          const file = item as DataRoomFile;
          return (
            file &&
            typeof file === 'object' &&
            'dataRoomFileId' in file &&
            !file.markedForDeletion
          );
        },
      ) || [];

    // Match linked file IDs to full document objects
    const linkedDocuments = allDocuments.filter((doc) =>
      linkedFileIds.has(doc.dataRoomFileId),
    );

    // Merge linked documents with existing selectedDocuments
    // This ensures all linked documents are shown, and manually selected ones are preserved
    if (linkedDocuments.length > 0) {
      setSelectedDocuments((prev) => {
        // Create a map of existing documents by ID
        const existingDocsMap = new Map(prev.map((d) => [d.dataRoomFileId, d]));

        // Add/update linked documents
        linkedDocuments.forEach((linkedDoc) => {
          existingDocsMap.set(linkedDoc.dataRoomFileId, linkedDoc);
        });

        // Convert back to array
        const merged = Array.from(existingDocsMap.values());

        // Check if anything changed
        const prevIds = new Set(prev.map((d) => d.dataRoomFileId));
        const mergedIds = new Set(merged.map((d) => d.dataRoomFileId));

        // If they're the same, don't update
        if (
          prevIds.size === mergedIds.size &&
          Array.from(mergedIds).every((id) => prevIds.has(id))
        ) {
          return prev;
        }

        // Otherwise, return merged documents
        return merged;
      });
    }
  }, [askSession, workspaceData]);

  /**
   * Load workspace data on mount.
   */
  useEffect(() => {
    getWorkspaceData();
  }, [getWorkspaceData]);

  /**
   * Load session data when sessionId changes.
   */
  useEffect(() => {
    if (sessionId) {
      fetchAskSessionData();
    }
  }, [fetchAskSessionData, sessionId]);

  useEffect(() => {
    fetchWorkspaceAskSessions();
  }, [fetchWorkspaceAskSessions]);

  /**
   * Show persona selection modal when there are no messages and no sessionId.
   */
  useEffect(() => {
    if (
      !sessionId &&
      messages.length === 0 &&
      !isLoading &&
      !error &&
      workspaceData
    ) {
      setShowPersonaModal(true);
    }
  }, [sessionId, messages.length, isLoading, error, workspaceData]);

  /**
   * Fetch personas when persona modal is shown.
   */
  useEffect(() => {
    if (showPersonaModal && personas.length === 0 && !isLoadingPersonas) {
      const fetchPersonas = async () => {
        try {
          setIsLoadingPersonas(true);
          setPersonaError(null);
          const fetchedPersonas = await getAskPersonas({ getAccessToken });
          setPersonas(fetchedPersonas);
        } catch (err) {
          const error =
            err instanceof Error ? err : new Error('Failed to load personas');
          setPersonaError(error);
          const response =
            (err as { response?: unknown; body?: unknown })?.response ??
            (err as { response?: unknown; body?: unknown })?.body;
          handleError(
            error,
            'Failed to load personas',
            response,
            enableAskToasts,
          );
          console.error('Failed to load personas:', error);
        } finally {
          setIsLoadingPersonas(false);
        }
      };
      fetchPersonas();
    }
  }, [
    showPersonaModal,
    personas.length,
    isLoadingPersonas,
    getAccessToken,
    handleError,
    enableAskToasts,
  ]);

  /**
   * Update highlighted message when search query changes.
   */
  useEffect(() => {
    if (searchQuery.trim() && searchMatches.length > 0) {
      setSearchMatchIndex(0);
      setHighlightedMessageId(searchMatches[0]);
    } else {
      setHighlightedMessageId(undefined);
    }
  }, [searchQuery, searchMatches]);

  /**
   * Load templates from localStorage on mount.
   */
  useEffect(() => {
    const savedTemplates = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    if (savedTemplates) {
      try {
        setTemplates(JSON.parse(savedTemplates));
      } catch (e) {
        console.error('Failed to load templates:', e);
      }
    }
  }, []);

  /**
   * Auto-save draft content to localStorage.
   */
  useEffect(() => {
    const draftKey = `${DRAFT_STORAGE_PREFIX}${sessionId || 'new'}`;
    const saveDraft = () => {
      if (textAreaRef.current) {
        const content = textAreaRef.current.getContent();
        if (content.trim()) {
          localStorage.setItem(draftKey, content);
        } else {
          localStorage.removeItem(draftKey);
        }
      }
    };

    const interval = setInterval(saveDraft, DRAFT_AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sessionId]);

  useEffect(() => {
    const storedTheme = localStorage.getItem('litium-theme-mode');
    if (storedTheme === 'compact' || storedTheme === 'default') {
      setThemeMode(storedTheme);
    }

    const handleThemeChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        mode?: 'default' | 'compact';
      }>;
      if (customEvent.detail?.mode) {
        setThemeMode(customEvent.detail.mode);
      }
    };

    window.addEventListener(
      'litium-theme-mode-change',
      handleThemeChange as EventListener,
    );
    return () => {
      window.removeEventListener(
        'litium-theme-mode-change',
        handleThemeChange as EventListener,
      );
    };
  }, []);

  /**
   * Load draft content on mount when sessionId is available.
   */
  useEffect(() => {
    if (!sessionId) return;
    const draftKey = `${DRAFT_STORAGE_PREFIX}${sessionId}`;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft && textAreaRef.current) {
      // Restore draft after a short delay to ensure component is ready
      setTimeout(() => {
        if (textAreaRef.current) {
          textAreaRef.current.setContent(savedDraft);
          textAreaRef.current.focus();
        }
      }, DRAFT_RESTORE_DELAY_MS);
    }
  }, [sessionId]);

  // ============= Event Handlers =============
  const {
    handleMessageClick,
    handlePinMessage,
    navigateSearchMatch,
    toggleSynopsis,
  } = useAskSessionNavigationHandlers({
    setPinnedMessageIds,
    setHighlightedMessageId,
    searchMatches,
    setSearchMatchIndex,
    setExpandedSynopsis,
  });

  /**
   * Handles viewing a document in the PDF viewer.
   *
   * @param doc - The document to view
   */
  const handleViewDocument = useCallback(
    async (doc: DataRoomFile) => {
      if (!workspaceId) return;

      setPdfViewerCitations(null);
      setSelectedDocumentForView(doc);
      setShowPdfViewerModal(true);
      setIsLoadingPdf(true);
      setPdfStreamUrl(null);

      try {
        const streamUrl = await downloadDocumentStream({
          getAccessToken,
          workspaceId: workspaceId,
          documentId: doc.dataRoomFileId,
          type: 'CONVERSION',
        });
        setPdfStreamUrl(streamUrl);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to load document');
        const response =
          (err as { response?: unknown; body?: unknown })?.response ??
          (err as { response?: unknown; body?: unknown })?.body;
        handleError(
          error,
          'Failed to load document',
          response,
          enableAskToasts,
        );
        console.error('Failed to load document:', error);
        setShowPdfViewerModal(false);
        setSelectedDocumentForView(null);
      } finally {
        setIsLoadingPdf(false);
      }
    },
    [workspaceId, getAccessToken, handleError, enableAskToasts],
  );

  /**
   * Opens the PDF viewer for a citation. Uses localStorage cache when the
   * stored document is for the same messageId; otherwise fetches and caches.
   */
  const handleCitationClick = useCallback(
    async (citation: AskSessionCitation, messageId: string) => {
      if (!sessionId) {
        handleError(
          new Error('Session not found.'),
          'Session not found.',
          undefined,
          enableAskToasts,
        );
        return;
      }

      const docForTitle: DataRoomFile = {
        name: citation.sourceName,
        dataRoomFileId: citation.source,
        dataRoomFileType: 'File',
        classifierTags: [],
        added: '',
        userId: '',
      };

      const message = messages.find((m) => m.id === messageId);
      setPdfViewerCitations(message?.citations ?? null);
      setInitialPdfPage(citation.pageNumber);
      setSelectedDocumentForView(docForTitle);
      setShowPdfViewerModal(true);
      setIsLoadingPdf(true);
      setPdfStreamUrl(null);

      try {
        const cachedUrl = getCitationDocumentFromCache(
          sessionId,
          citation.source,
          messageId,
        );
        if (cachedUrl) {
          setPdfStreamUrl(cachedUrl);
          return;
        }

        const { url, blob } = await getCitationDocument({
          getAccessToken,
          askSessionId: sessionId,
          askMessageId: messageId,
          documentId: citation.source,
        });
        setCitationDocumentCache(sessionId, citation.source, messageId, blob);
        setPdfStreamUrl(url);
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to load citation document');
        const response =
          (err as { response?: unknown; body?: unknown })?.response ??
          (err as { response?: unknown; body?: unknown })?.body;
        handleError(
          error,
          'Failed to load document',
          response,
          enableAskToasts,
        );
        console.error('Failed to load citation document:', error);
        setShowPdfViewerModal(false);
        setSelectedDocumentForView(null);
        setInitialPdfPage(null);
      } finally {
        setIsLoadingPdf(false);
      }
    },
    [sessionId, getAccessToken, handleError, enableAskToasts, messages],
  );

  /**
   * Handles persona selection and creates a new session.
   */
  const handlePersonaSubmit = useCallback(async () => {
    if (!selectedPersonaId || !workspaceId) return;

    try {
      setIsCreatingSession(true);
      setShowPersonaModal(false);

      // Generate title with timestamp and "LitiumTW Chat"
      const now = new Date();
      const timestampDate = new Intl.DateTimeFormat('en-GB').format(now);
      const timestampTime = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(now);
      const title = `Ask Session @ ${timestampDate}, ${timestampTime}`;

      // Create new session with selected persona
      const response = await createAskSession({
        getAccessToken,
        data: {
          workspaceId,
          title,
          askPersonaId: selectedPersonaId,
        },
      });

      setAskSession(response.askSession);
      setIsCreatingSession(false);

      // Navigate to the new session
      navigate(
        `/workspace/${workspaceId}/ask-session/${response.askSession.id}`,
        {
          replace: true,
        },
      );

      // Add initial bot messages to UI (use transform so citations are included)
      const currentUserId = currentUser?.userId || currentUser?.oid || '';
      const botMessages: AskMessage[] = response.initialBotMessages.map((msg) =>
        transformApiMessageToComponent(msg, currentUserId),
      );
      setMessages(botMessages);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to create session');
      console.error('Failed to create session:', error);
      const response =
        (err as { response?: unknown; body?: unknown })?.response ??
        (err as { response?: unknown; body?: unknown })?.body;
      handleError(error, 'Failed to create session', response, enableAskToasts);
      setShowPersonaModal(true); // Re-open modal on error
    } finally {
      setIsCreatingSession(false);
    }
  }, [
    selectedPersonaId,
    workspaceId,
    getAccessToken,
    navigate,
    handleError,
    enableAskToasts,
    currentUser?.userId,
    currentUser?.oid,
  ]);

  const clearSendTimeouts = useCallback(() => {
    if (invokeStartTimeoutRef.current) {
      clearTimeout(invokeStartTimeoutRef.current);
      invokeStartTimeoutRef.current = null;
    }
    if (fallbackRefreshTimeoutRef.current) {
      clearTimeout(fallbackRefreshTimeoutRef.current);
      fallbackRefreshTimeoutRef.current = null;
    }
  }, []);

  /**
   * Stops the in-flight Ask request and streaming UI. Shows "Generation cancelled" in chat.
   */
  const handleCancelGeneration = useCallback(() => {
    if (
      !assistantGenerationInProgressRef.current &&
      !isSendingMessage &&
      !sendInFlightRef.current
    ) {
      return;
    }

    generationCancelledRef.current = true;
    invokeAbortControllerRef.current?.abort();
    invokeAbortControllerRef.current = null;
    clearSendTimeouts();

    pendingSendRef.current = null;
    activeSendAttemptIdRef.current = null;
    sendInFlightRef.current = false;
    assistantGenerationInProgressRef.current = false;

    const streamingId = streamingMessageIdRef.current;
    const cancelledMessage: AskMessage = {
      id: `cancelled-${Date.now()}`,
      senderId: 'assistant',
      senderName: 'Assistant',
      content: GENERATION_CANCELLED_MESSAGE,
      timestamp: new Date(),
    };

    setMessages((prev) => {
      let next = prev;
      if (streamingId) {
        const streamingMsg = prev.find((m) => m.id === streamingId);
        if (streamingMsg && !streamingMsg.content.trim()) {
          next = prev.filter((m) => m.id !== streamingId);
        }
      }
      return [...next, cancelledMessage];
    });
    setScrollToBottomTrigger((t) => t + 1);

    setIsSendingMessage(false);
    setResponseStartTime(undefined);
    setProcessingStatus(null);
    setStreamingMessageId(null);
    lastStreamContentRef.current = '';
    displayedContentRef.current = '';
    toRevealRef.current = '';
    lastStreamActivityAtRef.current = 0;
  }, [clearSendTimeouts, isSendingMessage]);

  /**
   * Handles sending a message to the AI assistant.
   * Creates a new session if needed, or sends to existing session.
   *
   * @param segments - Array of content segments (text, documents, mentions)
   * @param documentIds - Optional array of document IDs
   * @param _mentionedUserIds - Optional array of mentioned user IDs (unused, handled in content)
   */
  const handleSendMessage = useCallback(
    async (
      segments: Segment[],
      documentIds?: string[],
      _mentionedUserIds?: string[],
    ) => {
      if (!workspaceId) return;

      // Prevent overlapping sends (double-click / fast Enter can race setState).
      if (assistantGenerationInProgressRef.current || sendInFlightRef.current) {
        pendingSendRef.current = {
          segments,
          documentIds,
          mentionedUserIds: _mentionedUserIds,
        };
        return;
      }
      sendInFlightRef.current = true;
      assistantGenerationInProgressRef.current = true;

      try {
        // Mentions are handled in the content text, not as separate parameters
        void _mentionedUserIds;

        const GROUP_JOIN_TIMEOUT_MS = 8000;
        // Refresh messages only after we've had enough time for streaming
        // to begin, to avoid replacing in-flight streamed messages.
        const FALLBACK_REFRESH_DELAY_MS = 22000;
        const INVOKE_START_WATCHDOG_MS = GROUP_JOIN_TIMEOUT_MS + 2000;

        // Build content from segments and collect document IDs (from document segments or [Name] in text)
        let content = '';
        const segmentDocumentIds: string[] = [];
        let hasMentions = false;

        segments.forEach((segment) => {
          if (segment.type === 'document') {
            const docId = String(segment.id).trim();
            if (docId) segmentDocumentIds.push(docId);
            content += `[${segment.name}] `;
          } else if (segment.type === 'person') {
            content += `@${segment.name} `;
            hasMentions = true;
          } else if (segment.type === 'text') {
            const textContent = segment.content;
            content += textContent;
            if (textContent.includes('@')) hasMentions = true;
            // Resolve [Document name] in text to IDs (fallback when segment was parsed as text)
            const bracketMatches = textContent.matchAll(/\[([^\]]+)\]/g);
            for (const match of bracketMatches) {
              const name = match[1].trim();
              const id = documentNameToIdMap.get(name);
              if (id) segmentDocumentIds.push(id);
            }
          }
        });

        // Only send document IDs that are actually in this message (inserted via # or resolved from [Name]).
        const selectedDocumentsForRequest = [
          ...new Set([...segmentDocumentIds, ...(documentIds || [])]),
        ].filter(Boolean);

        const trimmedContent = content.trim();
        if (!trimmedContent) return;

        // Determine askType based on whether message contains mentions
        const askType = hasMentions
          ? AskType.USER_INTERNAL_MESSAGE
          : AskType.MESSAGE;

        // If no session exists, show persona modal
        if (!sessionId && !isCreatingSession) {
          setShowPersonaModal(true);
          return;
        }

        if (sessionId) {
          generationCancelledRef.current = false;
          invokeAbortControllerRef.current?.abort();
          invokeAbortControllerRef.current = new AbortController();

          const sendAttemptId = `${Date.now()}-${Math.random()
            .toString(16)
            .slice(2)}`;
          activeSendAttemptIdRef.current = sendAttemptId;
          invokeStartedRef.current = false;

          if (invokeStartTimeoutRef.current) {
            clearTimeout(invokeStartTimeoutRef.current);
            invokeStartTimeoutRef.current = null;
          }

          // Send message to existing session
          setStreamingMessageId(null); // clear so typing indicator shows until first stream event
          setIsSendingMessage(true);
          setResponseStartTime(Date.now());
          setProcessingStatus(null);

          // Watchdog: if we haven't reached `invokeAsk` within a reasonable time,
          // the send attempt is likely stuck (e.g. SignalR group join never resolves).
          invokeStartTimeoutRef.current = setTimeout(() => {
            if (
              activeSendAttemptIdRef.current === sendAttemptId &&
              !invokeStartedRef.current
            ) {
              console.warn(
                'invokeAsk did not start in time; continuing with guarded invoke.',
              );
            }
          }, INVOKE_START_WATCHDOG_MS);

          // Add user message to UI immediately
          // (The assistant message itself will be streamed / refreshed separately.)
          const userMessage: AskMessage = {
            id: Date.now().toString(),
            senderId: 'currentUser',
            senderName: 'You',
            content: trimmedContent,
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, userMessage]);
          setScrollToBottomTrigger((t) => t + 1);

          try {
            // Before invoking Ask, try to ensure SignalR group join.
            // If this hangs, we still proceed to call `invokeAsk` so we don't get stuck.
            await withTimeout(
              ensureAskSessionGroupJoin(),
              GROUP_JOIN_TIMEOUT_MS,
              'ensureAskSessionGroupJoin',
            ).catch((err) => {
              console.warn(
                'Timed out ensuring Ask session group join; continuing invokeAsk.',
                err,
              );
            });

            const currentUserId = currentUser?.userId || currentUser?.oid || '';

            // Fallback refresh: if streaming never starts, pull latest conversation state.
            // This prevents the UI from getting stuck on TypingIndicator messages.
            fallbackRefreshTimeoutRef.current = setTimeout(async () => {
              const isStreaming = !!streamingMessageIdRef.current;
              const STREAM_STALE_UNLOCK_MS = 25000;
              const isStale =
                isStreaming &&
                Date.now() - (lastStreamActivityAtRef.current ?? 0) >=
                  STREAM_STALE_UNLOCK_MS;

              // If we are streaming and still receiving updates, don't interfere.
              if (isStreaming && !isStale) return;

              try {
                const updatedMessages = await getAskSessionMessages({
                  getAccessToken,
                  askSessionId: sessionId,
                });
                const transformedMessages = updatedMessages.map((msg) =>
                  transformApiMessageToComponent(msg, currentUserId),
                );
                setMessages(transformedMessages);

                // If streaming never started and we still have no assistant
                // content, stop the typing indicator and mark the send as failed.
                const hasAssistantContent = transformedMessages.some(
                  (m) =>
                    m.senderId === 'assistant' &&
                    typeof m.content === 'string' &&
                    m.content.trim().length > 0,
                );

                if (!hasAssistantContent) {
                  setFailedMessageIds((prev) => {
                    const next = new Set(prev);
                    next.add(userMessage.id);
                    return next;
                  });
                  assistantGenerationInProgressRef.current = false;
                  setIsSendingMessage(false);
                  setResponseStartTime(undefined);
                  setProcessingStatus(null);
                  setStreamingMessageId(null);
                  lastStreamContentRef.current = '';
                  displayedContentRef.current = '';
                  toRevealRef.current = '';
                } else {
                  // Streaming appears stuck but we already have some assistant content.
                  // Unlock the UI to prevent an indefinite "Still processing..." state.
                  assistantGenerationInProgressRef.current = false;
                  setIsSendingMessage(false);
                  setResponseStartTime(undefined);
                  setProcessingStatus(null);
                  setStreamingMessageId(null);
                  lastStreamContentRef.current = '';
                  displayedContentRef.current = '';
                  toRevealRef.current = '';
                }
              } catch (refreshError) {
                console.error(
                  'Failed to refresh messages as fallback:',
                  refreshError,
                );
              }
            }, FALLBACK_REFRESH_DELAY_MS);

            // Invoke Ask to get bot response.
            // Streaming should arrive via SignalR events.
            invokeStartedRef.current = true;
            if (invokeStartTimeoutRef.current) {
              clearTimeout(invokeStartTimeoutRef.current);
              invokeStartTimeoutRef.current = null;
            }

            await invokeAsk({
              getAccessToken,
              askSessionId: sessionId,
              data: {
                input: trimmedContent,
                inputTypeIndex: 0,
                variables: [],
                selectedDocuments: selectedDocumentsForRequest,
                documentCollections: [],
                workspaceId: workspaceId || '',
                askPromptId: askSession?.initialAskPromptId || '',
                askType: askType,
              },
              signal: invokeAbortControllerRef.current?.signal,
            });
          } catch (err) {
            if (isAbortError(err)) return;

            const error =
              err instanceof Error ? err : new Error('Failed to send message');
            console.error('Failed to send message to session:', err);

            // Mark message as failed instead of removing it.
            setFailedMessageIds((prev) => new Set([...prev, userMessage.id]));

            // Stop typing indicator/input lock because we won't get a stream.
            assistantGenerationInProgressRef.current = false;
            setIsSendingMessage(false);
            setResponseStartTime(undefined);
            setProcessingStatus(null);
            setStreamingMessageId(null);
            lastStreamContentRef.current = '';
            displayedContentRef.current = '';
            toRevealRef.current = '';

            const response =
              (err as { response?: unknown; body?: unknown })?.response ??
              (err as { response?: unknown; body?: unknown })?.body;
            handleError(
              error,
              'Failed to send message',
              response,
              enableAskToasts,
            );
          }
        }
      } catch (err) {
        if (isAbortError(err)) return;

        const error =
          err instanceof Error ? err : new Error('Failed to send message');
        console.error('Failed to send message:', err);

        assistantGenerationInProgressRef.current = false;
        setIsSendingMessage(false);
        setResponseStartTime(undefined);
        setProcessingStatus(null);
        setStreamingMessageId(null);
        const response =
          (err as { response?: unknown; body?: unknown })?.response ??
          (err as { response?: unknown; body?: unknown })?.body;
        handleError(error, 'Failed to send message', response, enableAskToasts);
      } finally {
        sendInFlightRef.current = false;
        if (!generationCancelledRef.current) {
          invokeAbortControllerRef.current = null;
        }
      }
    },
    [
      workspaceId,
      sessionId,
      isCreatingSession,
      getAccessToken,
      currentUser,
      askSession,
      documentNameToIdMap,
      ensureAskSessionGroupJoin,
      handleError,
      enableAskToasts,
    ],
  );

  // If a user attempted to send another message while one was still streaming,
  // we queue it in `pendingSendRef`. Once streaming completes, unlock and flush.
  useEffect(() => {
    if (isSendingMessage) return;
    if (!pendingSendRef.current) return;
    if (!workspaceId || !sessionId) return;

    const next = pendingSendRef.current;
    pendingSendRef.current = null;

    void handleSendMessage(
      next.segments,
      next.documentIds,
      next.mentionedUserIds,
    );
  }, [isSendingMessage, workspaceId, sessionId, handleSendMessage]);

  /**
   * Exports the conversation to a text file.
   */
  const exportConversation = useCallback(() => {
    const exportText = messages
      .map((msg) => {
        const timestamp = msg.timestamp.toLocaleString();
        const tokenInfo = msg.tokenUsage
          ? ` (${calculateTotalTokens(msg.tokenUsage)} tokens)`
          : '';
        return `[${timestamp}] ${msg.senderName}: ${msg.content}${tokenInfo}`;
      })
      .join('\n\n');

    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    if (enableAskToasts) {
      handleSuccess('Conversation exported successfully');
    }
  }, [messages, handleSuccess, enableAskToasts]);

  /**
   * Retries sending a failed message.
   *
   * @param messageId - The ID of the failed message to retry
   */
  const handleRetryMessage = useCallback(
    async (messageId: string) => {
      const failedMessage = messages.find((msg) => msg.id === messageId);
      if (!failedMessage || !workspaceId || !sessionId) return;

      // Remove from failed list
      setFailedMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });

      // Remove the failed message
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));

      // Retry sending
      try {
        setStreamingMessageId(null); // clear so typing indicator shows until first stream event
        setIsSendingMessage(true);
        setResponseStartTime(Date.now());
        setProcessingStatus(null);

        await invokeAsk({
          getAccessToken,
          askSessionId: sessionId,
          data: {
            input: failedMessage.content,
            inputTypeIndex: 0,
            variables: [],
            selectedDocuments: [],
            documentCollections: [],
            workspaceId: workspaceId || '',
            askPromptId: askSession?.initialAskPromptId || '',
            askType: AskType.MESSAGE,
          },
        });

        // Refresh messages
        const updatedMessages = await getAskSessionMessages({
          getAccessToken,
          askSessionId: sessionId,
        });

        const currentUserId = currentUser?.userId || currentUser?.oid || '';
        const transformedMessages = transformApiMessages(
          updatedMessages,
          currentUserId,
        );

        setMessages(transformedMessages);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to retry message');
        const response =
          (err as { response?: unknown; body?: unknown })?.response ??
          (err as { response?: unknown; body?: unknown })?.body;
        handleError(
          error,
          'Failed to retry message',
          response,
          enableAskToasts,
        );
        // Re-add to failed list
        setFailedMessageIds((prev) => new Set([...prev, messageId]));
      } finally {
        setIsSendingMessage(false);
        setResponseStartTime(undefined);
      }
    },
    [
      messages,
      workspaceId,
      sessionId,
      getAccessToken,
      askSession,
      currentUser,
      handleError,
      enableAskToasts,
    ],
  );

  /**
   * Saves templates to localStorage and state.
   *
   * @param newTemplates - Array of templates to save
   */
  const saveTemplates = useCallback((newTemplates: MessageTemplate[]) => {
    setTemplates(newTemplates);
    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(newTemplates));
  }, []);

  /**
   * Handles keyboard shortcuts for the component.
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus input
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === KEYBOARD_SHORTCUTS.FOCUS_INPUT
      ) {
        e.preventDefault();
        if (textAreaRef.current) {
          textAreaRef.current.focus();
        }
      }
      // Ctrl/Cmd + F to search
      if ((e.ctrlKey || e.metaKey) && e.key === KEYBOARD_SHORTCUTS.SEARCH) {
        e.preventDefault();
        setShowSearch(true);
      }
      // Escape to clear input or close search
      if (e.key === KEYBOARD_SHORTCUTS.ESCAPE) {
        if (showSearch) {
          setShowSearch(false);
          setSearchQuery('');
        } else if (showHelpModal) {
          setShowHelpModal(false);
        } else if (showTemplatesModal) {
          setShowTemplatesModal(false);
        } else if (textAreaRef.current) {
          textAreaRef.current.focus();
        }
      }
      // Help shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === KEYBOARD_SHORTCUTS.HELP) {
        e.preventDefault();
        setShowHelpModal(true);
      }
      // Templates shortcut
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === KEYBOARD_SHORTCUTS.TEMPLATES &&
        !e.shiftKey
      ) {
        e.preventDefault();
        setShowTemplatesModal(true);
      }
      // Search navigation
      if (showSearch && searchMatches.length > 0) {
        if (e.key === KEYBOARD_SHORTCUTS.ENTER && e.shiftKey) {
          e.preventDefault();
          navigateSearchMatch('prev');
        } else if (e.key === KEYBOARD_SHORTCUTS.ENTER) {
          e.preventDefault();
          navigateSearchMatch('next');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    showSearch,
    showHelpModal,
    showTemplatesModal,
    searchMatches,
    navigateSearchMatch,
  ]);

  // ============= Render Helpers =============
  const { getModifierKey, preparedDocuments, preparedPeople } =
    useAskSessionRenderHelpers({
      isMac,
      workspaceData,
    });

  // ============= Early Returns =============

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentSectionContainer>
          <ContentSection>
            Error loading workspace: {error.message}
          </ContentSection>
        </ContentSectionContainer>
      </PageContainer>
    );
  }

  // ============= Render =============

  return (
    <PageContainer>
      {!isLoading && !error && workspaceData && (
        <>
          <WorkspaceHeaderLayout
            workspaceData={workspaceData}
            activePage="ask-session"
            askSessionTitle={askSession?.askTitle || 'Untitled Session'}
          />
          <ContentSectionContainer>
            {themeMode === 'compact' && (
              <LeftSidebarContainer $open={isLeftSidebarOpen}>
                <SessionsSidebar>
                  <SessionsSidebarHeader>
                    <SessionsSidebarTitle>Ask Sessions</SessionsSidebarTitle>
                    <SidebarToggleButton
                      onClick={() => setIsLeftSidebarOpen(false)}
                      title="Hide sessions panel"
                    >
                      Hide Sessions
                    </SidebarToggleButton>
                  </SessionsSidebarHeader>
                  <SessionList>
                    {groupedWorkspaceAskSessions.map((group) => (
                      <SessionDateGroup key={group.label}>
                        <SessionDateGroupTitle>
                          {group.label}
                        </SessionDateGroupTitle>
                        {group.sessions.map((session) => (
                          <SessionListItem
                            key={session.id}
                            $active={session.id === sessionId}
                            onClick={() =>
                              navigate(
                                `/workspace/${workspaceId}/ask-session/${session.id}`,
                              )
                            }
                          >
                            <SessionListItemTitle>
                              {session.askThreadSynopsis?.trim()
                                ? truncateText(session.askThreadSynopsis, 72)
                                : session.askTitle || 'Untitled Session'}
                            </SessionListItemTitle>
                          </SessionListItem>
                        ))}
                      </SessionDateGroup>
                    ))}
                  </SessionList>
                </SessionsSidebar>
              </LeftSidebarContainer>
            )}

            <RightSidebarContainer $open={isRightSidebarOpen}>
              <ContextPanel data-tour="ask-session-context-panel">
                {themeMode === 'compact' && (
                  <ToolsPanelHeader>
                    <SessionsSidebarTitle>Tools</SessionsSidebarTitle>
                    <SidebarToggleButton
                      onClick={() => setIsRightSidebarOpen(false)}
                      title="Hide tools panel"
                    >
                      Hide Tools
                    </SidebarToggleButton>
                  </ToolsPanelHeader>
                )}
                {themeMode !== 'compact' && (
                  <>
                    <Headers headerType="h2">
                      {askSession?.askTitle || 'Untitled Session'}
                    </Headers>
                    {/* Thread Summary */}
                    <ThreadSummary>
                      <ThreadSummaryHeader>
                        <Headers headerType="h3">Thread Summary</Headers>
                      </ThreadSummaryHeader>
                      <ThreadSummaryContent>
                        <ThreadSummaryItem>
                          <ThreadSummaryItemContent>
                            {askSession?.askThreadSynopsis ||
                              'No Synopsis available'}
                          </ThreadSummaryItemContent>
                        </ThreadSummaryItem>
                      </ThreadSummaryContent>
                    </ThreadSummary>
                  </>
                )}

                {/* Active Documents */}
                <ActiveDocuments data-tour="ask-session-active-documents">
                  <ActiveDocumentsHeader>
                    <Headers headerType="h3">📄 Active Documents</Headers>
                    {selectedDocuments.length > 0 && (
                      <ExpandToggleButton
                        onClick={() =>
                          setActiveDocumentsExpanded(!activeDocumentsExpanded)
                        }
                        title={activeDocumentsExpanded ? 'Collapse' : 'Expand'}
                      >
                        {activeDocumentsExpanded ? (
                          <RiCollapseDiagonal2Fill />
                        ) : (
                          <RiExpandDiagonal2Fill />
                        )}
                      </ExpandToggleButton>
                    )}
                  </ActiveDocumentsHeader>

                  <UploadProcessingIndicator
                    uploadingFileNames={uploadingFileNames}
                  />

                  {/* Add Document Button - Always at top */}
                  <AddDocumentButtonWrapper>
                    <AddDocumentButton
                      onClick={() => setShowDocumentSelector(true)}
                      data-tour="ask-session-add-document-button"
                    >
                      <AddDocumentText>
                        {selectedDocuments.length === 0
                          ? '+ Add Document'
                          : '+ Add More Documents'}
                      </AddDocumentText>
                    </AddDocumentButton>
                  </AddDocumentButtonWrapper>

                  {selectedDocuments.length > 0 && (
                    <ActiveDocumentsList $expanded={activeDocumentsExpanded}>
                      {activeDocumentsExpanded ? (
                        // Expanded view - full details
                        selectedDocuments.map((doc) => {
                          const fileExtension = getFileExtension(doc.name);
                          const isSynopsisExpanded =
                            expandedSynopsis[doc.dataRoomFileId] || false;
                          const synopsisNeedsTruncation =
                            doc.synopsis &&
                            doc.synopsis.length > MAX_SYNOPSIS_PREVIEW_LENGTH;

                          // Check if document is linked (from askSession.files)
                          const isLinked = askSession?.files
                            ? askSession.files.some((file) => {
                                const fileId =
                                  typeof file === 'string'
                                    ? file
                                    : (file as DataRoomFile)?.dataRoomFileId ||
                                      (file as DataRoomFile)?.id;
                                return fileId === doc.dataRoomFileId;
                              })
                            : false;

                          return (
                            <ActiveDocumentsItem
                              key={doc.dataRoomFileId}
                              data-tour="ask-session-active-document-item"
                            >
                              <DocumentCardClickable
                                onClick={() => handleViewDocument(doc)}
                              >
                                <DocumentName>
                                  {doc.name || 'Untitled Document'}
                                </DocumentName>
                                <DocumentMeta>
                                  {doc.readableSize && (
                                    <span>
                                      {fileExtension} • {doc.readableSize}
                                    </span>
                                  )}
                                  {!doc.readableSize && fileExtension && (
                                    <span>{fileExtension}</span>
                                  )}
                                </DocumentMeta>
                                {doc.synopsis && (
                                  <div>
                                    <SynopsisText
                                      $expanded={isSynopsisExpanded}
                                    >
                                      {doc.synopsis}
                                    </SynopsisText>
                                    {synopsisNeedsTruncation && (
                                      <SynopsisToggle
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleSynopsis(doc.dataRoomFileId);
                                        }}
                                      >
                                        {isSynopsisExpanded
                                          ? 'Show less'
                                          : 'Show more'}
                                      </SynopsisToggle>
                                    )}
                                  </div>
                                )}
                                {doc.added && (
                                  <DocumentAddedDate>
                                    Added {formatDate(doc.added)}
                                  </DocumentAddedDate>
                                )}
                                {isLinked && (
                                  <LinkedBadge data-tour="ask-session-active-document-linked-badge">
                                    💬 Linked
                                  </LinkedBadge>
                                )}
                                <RemoveDocumentButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDocuments((prev) =>
                                      prev.filter(
                                        (d) =>
                                          d.dataRoomFileId !==
                                          doc.dataRoomFileId,
                                      ),
                                    );
                                  }}
                                  title="Remove document"
                                >
                                  ×
                                </RemoveDocumentButton>
                              </DocumentCardClickable>
                            </ActiveDocumentsItem>
                          );
                        })
                      ) : (
                        // Compact view - icons only
                        <CompactDocumentsGrid>
                          {selectedDocuments.map((doc) => {
                            const isLinked = askSession?.files
                              ? askSession.files.some((file) => {
                                  const fileId =
                                    typeof file === 'string'
                                      ? file
                                      : (file as DataRoomFile)
                                          ?.dataRoomFileId ||
                                        (file as DataRoomFile)?.id;
                                  return fileId === doc.dataRoomFileId;
                                })
                              : false;

                            return (
                              <CompactDocumentIcon
                                key={doc.dataRoomFileId}
                                onClick={() => handleViewDocument(doc)}
                                title={doc.name || 'Untitled Document'}
                                $linked={isLinked}
                              >
                                <CompactDocumentContent>
                                  <DocumentIcon>
                                    {getFileIcon(doc.name, doc.mimeType)}
                                  </DocumentIcon>
                                  <CompactDocumentLabel>
                                    {truncateText(doc.name || 'File', 15)}
                                  </CompactDocumentLabel>
                                </CompactDocumentContent>
                                {isLinked && (
                                  <LinkedIndicator data-tour="ask-session-active-document-linked-badge">
                                    💬
                                  </LinkedIndicator>
                                )}
                              </CompactDocumentIcon>
                            );
                          })}
                          {uploadingFileNames
                            .filter(
                              (fileName) =>
                                !selectedDocuments.some(
                                  (doc) =>
                                    (doc.name || '').toLowerCase() ===
                                    fileName.toLowerCase(),
                                ),
                            )
                            .map((fileName) => (
                              <CompactDocumentIcon
                                key={`uploading-${fileName}`}
                                title={`${fileName} (uploading...)`}
                                $linked={false}
                              >
                                <CompactDocumentContent>
                                  <DocumentIcon>
                                    <LoadingSpinner size="small" />
                                  </DocumentIcon>
                                  <CompactDocumentLabel>
                                    {getFileExtension(fileName) ||
                                      fileName ||
                                      'Uploading'}
                                  </CompactDocumentLabel>
                                </CompactDocumentContent>
                              </CompactDocumentIcon>
                            ))}
                        </CompactDocumentsGrid>
                      )}
                    </ActiveDocumentsList>
                  )}
                </ActiveDocuments>

                {/* Keyboard Shortcuts */}
                <div data-tour="ask-session-keyboard-shortcuts">
                  <Accordion
                    title="⌨️ Keyboard Shortcuts"
                    extras={{ defaultExpanded: false }}
                  >
                    <KeyboardShortcutsContent>
                      <ShortcutItem
                        onClick={() => {
                          if (textAreaRef.current) {
                            textAreaRef.current.focus();
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <ShortcutDescription>
                          Focus input field
                        </ShortcutDescription>
                        <ShortcutKeysContainer>
                          <ShortcutKey>{getModifierKey()}</ShortcutKey>
                          <ShortcutKey>K</ShortcutKey>
                        </ShortcutKeysContainer>
                      </ShortcutItem>
                      <ShortcutItem
                        onClick={() => setShowSearch(true)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ShortcutDescription>
                          Search messages
                        </ShortcutDescription>
                        <ShortcutKeysContainer>
                          <ShortcutKey>{getModifierKey()}</ShortcutKey>
                          <ShortcutKey>F</ShortcutKey>
                        </ShortcutKeysContainer>
                      </ShortcutItem>
                      <ShortcutItem
                        onClick={() => {
                          if (showSearch) {
                            setShowSearch(false);
                            setSearchQuery('');
                          } else if (showHelpModal) {
                            setShowHelpModal(false);
                          } else if (showTemplatesModal) {
                            setShowTemplatesModal(false);
                          } else if (textAreaRef.current) {
                            textAreaRef.current.focus();
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <ShortcutDescription>
                          Clear input / Close search
                        </ShortcutDescription>
                        <ShortcutKey>Esc</ShortcutKey>
                      </ShortcutItem>
                      <ShortcutItem>
                        <ShortcutDescription>
                          Select multiple messages
                        </ShortcutDescription>
                        <ShortcutKeysContainer>
                          <ShortcutKey>{getModifierKey()}</ShortcutKey>
                          <ShortcutClickHint>+ Click</ShortcutClickHint>
                        </ShortcutKeysContainer>
                      </ShortcutItem>
                      <ShortcutItem
                        onClick={() => setShowHelpModal(true)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ShortcutDescription>Show help</ShortcutDescription>
                        <ShortcutKeysContainer>
                          <ShortcutKey>{getModifierKey()}</ShortcutKey>
                          <ShortcutKey>/</ShortcutKey>
                        </ShortcutKeysContainer>
                      </ShortcutItem>
                      <ShortcutItem
                        onClick={() => setShowTemplatesModal(true)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ShortcutDescription>
                          Show templates
                        </ShortcutDescription>
                        <ShortcutKeysContainer>
                          <ShortcutKey>{getModifierKey()}</ShortcutKey>
                          <ShortcutKey>T</ShortcutKey>
                        </ShortcutKeysContainer>
                      </ShortcutItem>
                    </KeyboardShortcutsContent>
                  </Accordion>
                </div>

                {/* Pinned Messages */}
                {pinnedMessagesList.length > 0 && (
                  <PinnedMessages>
                    <PinnedMessagesHeader>
                      📌 PINNED MESSAGES
                    </PinnedMessagesHeader>
                    <PinnedMessagesContent>
                      {pinnedMessagesList.map((msg) => (
                        <PinnedMessageItem
                          key={msg.id}
                          onClick={() => handleMessageClick(msg.id)}
                        >
                          <PinnedMessagePreview>
                            {truncateText(
                              msg.content,
                              MAX_PINNED_MESSAGE_PREVIEW_LENGTH,
                            )}
                          </PinnedMessagePreview>
                          <PinnedMessageMeta>
                            <span>{msg.senderName}</span>
                            <span>{formatDate(msg.timestamp)}</span>
                          </PinnedMessageMeta>
                        </PinnedMessageItem>
                      ))}
                    </PinnedMessagesContent>
                  </PinnedMessages>
                )}

                {/* Export Button */}
                <ExportButtonContainer>
                  <ExportButton onClick={exportConversation}>
                    📥 Export Conversation
                  </ExportButton>
                </ExportButtonContainer>
              </ContextPanel>
            </RightSidebarContainer>

            <ContentSection>
              {themeMode === 'compact' && (
                <ChatTopBar>
                  {!isLeftSidebarOpen && (
                    <SidebarToggleButton
                      onClick={() => setIsLeftSidebarOpen(true)}
                    >
                      Show Sessions
                    </SidebarToggleButton>
                  )}
                  {!isRightSidebarOpen && (
                    <SidebarToggleButton
                      onClick={() => setIsRightSidebarOpen(true)}
                    >
                      Show Tools
                    </SidebarToggleButton>
                  )}
                </ChatTopBar>
              )}
              {/* Chat Container */}
              {/* Search Bar */}
              {showSearch && (
                <SearchBar>
                  <SearchInput
                    type="text"
                    placeholder="Search messages... (Enter: next, Shift+Enter: prev)"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchMatchIndex(0);
                    }}
                    autoFocus
                  />
                  {searchMatches.length > 0 && (
                    <SearchMatchCount>
                      {searchMatchIndex + 1} / {searchMatches.length}
                    </SearchMatchCount>
                  )}
                  <SearchNavButton
                    $disabled={searchMatches.length === 0}
                    onClick={() => navigateSearchMatch('prev')}
                    disabled={searchMatches.length === 0}
                    title="Previous match (Shift+Enter)"
                  >
                    ↑
                  </SearchNavButton>
                  <SearchNavButton
                    $disabled={searchMatches.length === 0}
                    onClick={() => navigateSearchMatch('next')}
                    disabled={searchMatches.length === 0}
                    title="Next match (Enter)"
                  >
                    ↓
                  </SearchNavButton>
                  <SearchCloseButton
                    onClick={() => {
                      setShowSearch(false);
                      setSearchQuery('');
                      setHighlightedMessageId(undefined);
                    }}
                  >
                    Close
                  </SearchCloseButton>
                </SearchBar>
              )}

              {/* Chat Messages */}
              <ChatMessages
                messages={showSearch ? filteredMessages : messages}
                currentUserId="currentUser"
                users={users}
                isTyping={isSendingMessage && !streamingMessageId}
                typingUserName="Assistant"
                responseStartTime={responseStartTime}
                processingStatus={processingStatus}
                scrollToBottomTrigger={scrollToBottomTrigger}
                onRetryMessage={handleRetryMessage}
                failedMessageIds={failedMessageIds}
                searchQuery={showSearch ? searchQuery : undefined}
                highlightedMessageId={highlightedMessageId}
                onMessageClick={handleMessageClick}
                pinnedMessageIds={pinnedMessageIds}
                onPinMessage={handlePinMessage}
                onCitationClick={handleCitationClick}
              />

              {/* Enhanced Text Area */}
              <EnhancedTextArea
                ref={textAreaRef}
                workspaceData={workspaceData}
                documents={preparedDocuments}
                selectedDocuments={selectedDocuments}
                messages={messages}
                currentUserId="currentUser"
                onSendMessage={handleSendMessage}
                linkedDocumentIds={
                  askSession?.files
                    ? askSession.files
                        .map((file) => {
                          const fileId =
                            typeof file === 'string'
                              ? file
                              : (file as DataRoomFile)?.dataRoomFileId ||
                                (file as DataRoomFile)?.id;
                          return fileId;
                        })
                        .filter((id): id is string => !!id)
                    : []
                }
                placeholder={
                  isCreatingSession
                    ? 'Creating session...'
                    : isSendingMessage
                      ? 'Sending message...'
                      : 'Type # for linked documents, @ to mention someone...'
                }
                isLoading={isCreatingSession || isSendingMessage}
                onCancelGeneration={
                  isSendingMessage ? handleCancelGeneration : undefined
                }
                disableEnterToSend={isEnterSendBlockedByTutorial}
                people={preparedPeople}
                commands={[...COMMAND_TEMPLATES]}
              />
            </ContentSection>
          </ContentSectionContainer>

          {/* Document Selector Modal */}
          <DocumentSelector
            isOpen={showDocumentSelector}
            onClose={() => setShowDocumentSelector(false)}
            workspaceId={workspaceId}
            selectedDocumentIds={selectedDocuments.map((d) => d.dataRoomFileId)}
            linkedDocumentIds={
              askSession?.files
                ? askSession.files
                    .map((file) => {
                      const fileId =
                        typeof file === 'string'
                          ? file
                          : (file as DataRoomFile)?.dataRoomFileId ||
                            (file as DataRoomFile)?.id;
                      return fileId;
                    })
                    .filter((id): id is string => !!id)
                : []
            }
            askSessionId={sessionId}
            uploadingFileNames={uploadingFileNames}
            onSelectDocuments={async (docs) => {
              if (!sessionId || !workspaceId) {
                // Can't link without session ID
                setSelectedDocuments((prev) => {
                  const existingIds = new Set(
                    prev.map((d) => d.dataRoomFileId),
                  );
                  const newDocs = docs.filter(
                    (d) => !existingIds.has(d.dataRoomFileId),
                  );
                  return [...prev, ...newDocs];
                });
                return;
              }

              // Merge with existing documents, avoiding duplicates
              const existingIds = new Set(
                selectedDocuments.map((d) => d.dataRoomFileId),
              );
              const newDocs = docs.filter(
                (d) => !existingIds.has(d.dataRoomFileId),
              );

              // Link each newly selected document to the ask session
              for (const doc of newDocs) {
                try {
                  await linkFileToAsk({
                    getAccessToken,
                    workspaceId,
                    body: {
                      dataRoomFileId: doc.dataRoomFileId,
                      instanceId: sessionId,
                    },
                  });
                } catch (err) {
                  console.error(
                    `Failed to link file ${doc.dataRoomFileId} to ask session:`,
                    err,
                  );
                  const error =
                    err instanceof Error
                      ? err
                      : new Error(
                          `Failed to link ${doc.name || 'document'} to ask session`,
                        );
                  const response =
                    (err as { response?: unknown; body?: unknown })?.response ??
                    (err as { response?: unknown; body?: unknown })?.body;
                  handleError(
                    error,
                    `Failed to link ${doc.name || 'document'} to ask session`,
                    response,
                    enableAskToasts,
                  );
                }
              }

              // Update selected documents
              setSelectedDocuments((prev) => [...prev, ...newDocs]);

              // Refresh session data to get updated linked files
              await fetchAskSessionData();
            }}
            onUploadStart={(fileNames) => {
              // Track files being uploaded (empty array means clear the list)
              if (fileNames.length === 0) {
                setUploadingFileNames([]);
              } else {
                setUploadingFileNames(fileNames);
              }
            }}
            onDocumentsUploaded={async () => {
              // Refresh workspace data and session data to get newly linked files
              await getWorkspaceData();
              if (sessionId) {
                await fetchAskSessionData();
              }
            }}
          />

          {/* PDF Viewer Modal */}
          <Modal
            isOpen={showPdfViewerModal && !!selectedDocumentForView}
            onClose={() => {
              setShowPdfViewerModal(false);
              setSelectedDocumentForView(null);
              setPdfStreamUrl(null);
              setInitialPdfPage(null);
              setPdfViewerCitations(null);
            }}
            title={selectedDocumentForView?.name || 'PDF Viewer'}
            size="xLarge"
          >
            {isLoadingPdf ? (
              <LoadingSpinner variant="fullPage" />
            ) : pdfStreamUrl ? (
              pdfViewerCitations && pdfViewerCitations.length > 0 ? (
                <PdfViewerLayout>
                  <PdfViewerMain>
                    <PdfViewer
                      ref={pdfViewerRef}
                      data={pdfStreamUrl}
                      initialPage={initialPdfPage ?? undefined}
                    />
                  </PdfViewerMain>
                  <PdfViewerCitationsContainer>
                    <PdfViewerCitationsPanelTitle>
                      Citations
                    </PdfViewerCitationsPanelTitle>
                    <PdfViewerCitationsPanel>
                      {pdfViewerCitations.map((citation) => {
                        const color = getCitationColor(citation.citationRef);
                        const snippet = citation.snippet?.trim() || '—';
                        return (
                          <PdfViewerCitationItem
                            key={citation.citationRef}
                            type="button"
                            $color={color}
                            onClick={() => {
                              pdfViewerRef.current?.setPage(
                                citation.pageNumber,
                              );
                            }}
                            title={`Page ${citation.pageNumber}${snippet !== '—' ? ` — ${snippet}` : ''}`}
                          >
                            <PdfViewerCitationRef $color={color}>
                              [{citation.citationRef}]
                            </PdfViewerCitationRef>
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.15rem',
                              }}
                            >
                              <PdfViewerCitationPage>
                                Page {citation.pageNumber}
                              </PdfViewerCitationPage>
                              <PdfViewerCitationSnippet title={snippet}>
                                {snippet}
                              </PdfViewerCitationSnippet>
                            </span>
                          </PdfViewerCitationItem>
                        );
                      })}
                    </PdfViewerCitationsPanel>
                  </PdfViewerCitationsContainer>
                </PdfViewerLayout>
              ) : (
                <PdfViewer
                  ref={pdfViewerRef}
                  data={pdfStreamUrl}
                  initialPage={initialPdfPage ?? undefined}
                />
              )
            ) : (
              <PdfViewerError>Failed to load document</PdfViewerError>
            )}
          </Modal>

          {/* Help Modal */}
          <Modal
            isOpen={showHelpModal}
            onClose={() => setShowHelpModal(false)}
            title="Keyboard Shortcuts & Help"
            size="medium"
          >
            <ModalContent>
              <ModalSectionTitle>Keyboard Shortcuts</ModalSectionTitle>
              <ModalSection>
                <ModalShortcutRow>
                  <span>Focus input field</span>
                  <ModalKbd>
                    {getModifierKey()}+
                    {KEYBOARD_SHORTCUTS.FOCUS_INPUT.toUpperCase()}
                  </ModalKbd>
                </ModalShortcutRow>
                <ModalShortcutRow>
                  <span>Search messages</span>
                  <ModalKbd>
                    {getModifierKey()}+{KEYBOARD_SHORTCUTS.SEARCH.toUpperCase()}
                  </ModalKbd>
                </ModalShortcutRow>
                <ModalShortcutRow>
                  <span>Show help</span>
                  <ModalKbd>
                    {getModifierKey()}+{KEYBOARD_SHORTCUTS.HELP}
                  </ModalKbd>
                </ModalShortcutRow>
                <ModalShortcutRow>
                  <span>Show templates</span>
                  <ModalKbd>
                    {getModifierKey()}+
                    {KEYBOARD_SHORTCUTS.TEMPLATES.toUpperCase()}
                  </ModalKbd>
                </ModalShortcutRow>
                <ModalShortcutRow>
                  <span>Clear input / Close</span>
                  <ModalKbd>{KEYBOARD_SHORTCUTS.ESCAPE}</ModalKbd>
                </ModalShortcutRow>
              </ModalSection>
              <ModalSectionTitle style={{ marginTop: '2rem' }}>
                Input Features
              </ModalSectionTitle>
              <ModalFeatureList>
                <div>
                  <strong>@</strong> - Mention a person
                </div>
                <div>
                  <strong>#</strong> - Insert a document or command
                </div>
                <div>
                  <strong>Ctrl/Cmd + Click</strong> - Select multiple messages
                </div>
              </ModalFeatureList>
            </ModalContent>
          </Modal>

          {/* Persona Selection Modal */}
          <Modal
            isOpen={showPersonaModal}
            onClose={() => {
              // Don't allow closing if no session exists - user must select a persona
              if (sessionId) {
                setShowPersonaModal(false);
              } else {
                // Go back in history if no session exists
                navigate(-1);
              }
            }}
            title="Select a Persona"
            size="large"
          >
            <PersonaModalContent data-tour="ask-persona-modal">
              {isLoadingPersonas ? (
                <PersonaLoadingContainer>
                  <LoadingSpinner variant="fullPage" />
                </PersonaLoadingContainer>
              ) : personaError ? (
                <PersonaErrorContainer>
                  <p>Failed to load personas: {personaError.message}</p>
                  <PersonaCancelButton
                    onClick={() => {
                      setPersonaError(null);
                      setIsLoadingPersonas(false);
                    }}
                  >
                    Retry
                  </PersonaCancelButton>
                </PersonaErrorContainer>
              ) : personas.length === 0 ? (
                <PersonaErrorContainer>
                  <p>No personas available</p>
                </PersonaErrorContainer>
              ) : (
                <>
                  <PersonaCardGroup>
                    {personas.map((persona) => (
                      <PersonaCard
                        key={persona.id}
                        $selected={selectedPersonaId === persona.id}
                        onClick={() => setSelectedPersonaId(persona.id)}
                        data-tour={`ask-persona-option-${persona.name}`}
                        data-persona-name={persona.name}
                      >
                        <PersonaIcon>🎭</PersonaIcon>
                        <PersonaName>{persona.name}</PersonaName>
                        {persona.description && (
                          <PersonaDescription>
                            {persona.description}
                          </PersonaDescription>
                        )}
                        {persona.tags && persona.tags.length > 0 && (
                          <PersonaTags>
                            {persona.tags.map((tag, index) => (
                              <PersonaTag key={index}>{tag}</PersonaTag>
                            ))}
                          </PersonaTags>
                        )}
                      </PersonaCard>
                    ))}
                  </PersonaCardGroup>
                  <PersonaModalActions>
                    <PersonaCancelButton
                      onClick={() => {
                        if (sessionId) {
                          setShowPersonaModal(false);
                        } else {
                          // Go back in history if no session exists
                          navigate(-1);
                        }
                      }}
                    >
                      Cancel
                    </PersonaCancelButton>
                    <PersonaSubmitButton
                      onClick={handlePersonaSubmit}
                      disabled={!selectedPersonaId || isCreatingSession}
                      data-tour="ask-persona-submit-button"
                    >
                      {isCreatingSession ? 'Creating Session...' : 'Submit'}
                    </PersonaSubmitButton>
                  </PersonaModalActions>
                </>
              )}
            </PersonaModalContent>
          </Modal>

          {/* Templates Modal */}
          <Modal
            isOpen={showTemplatesModal}
            onClose={() => setShowTemplatesModal(false)}
            title="Message Templates"
            size="medium"
          >
            <ModalContent>
              <TemplatesHeader>
                <h3>Templates</h3>
                <NewTemplateButton
                  onClick={() => {
                    const newTemplate: MessageTemplate = {
                      id: Date.now().toString(),
                      name: `Template ${templates.length + 1}`,
                      content: '',
                    };
                    saveTemplates([...templates, newTemplate]);
                  }}
                >
                  + New Template
                </NewTemplateButton>
              </TemplatesHeader>
              {templates.length === 0 ? (
                <EmptyTemplatesMessage>
                  No templates yet. Create one to get started!
                </EmptyTemplatesMessage>
              ) : (
                <TemplatesList>
                  {templates.map((template) => (
                    <TemplateItem key={template.id}>
                      <TemplateInput
                        type="text"
                        value={template.name}
                        onChange={(e) => {
                          const updated = templates.map((t) =>
                            t.id === template.id
                              ? { ...t, name: e.target.value }
                              : t,
                          );
                          saveTemplates(updated);
                        }}
                        placeholder="Template name"
                      />
                      <TemplateTextarea
                        value={template.content}
                        onChange={(e) => {
                          const updated = templates.map((t) =>
                            t.id === template.id
                              ? { ...t, content: e.target.value }
                              : t,
                          );
                          saveTemplates(updated);
                        }}
                        placeholder="Template content (use @ for mentions, # for documents)"
                        rows={3}
                      />
                      <TemplateActions>
                        <UseTemplateButton
                          onClick={() => {
                            if (textAreaRef.current) {
                              textAreaRef.current.setContent(template.content);
                              textAreaRef.current.focus();
                              setShowTemplatesModal(false);
                            }
                          }}
                        >
                          Use Template
                        </UseTemplateButton>
                        <DeleteTemplateButton
                          onClick={() => {
                            saveTemplates(
                              templates.filter((t) => t.id !== template.id),
                            );
                          }}
                        >
                          Delete
                        </DeleteTemplateButton>
                      </TemplateActions>
                    </TemplateItem>
                  ))}
                </TemplatesList>
              )}
            </ModalContent>
          </Modal>
        </>
      )}
    </PageContainer>
  );
};
